import { useState, useEffect } from "react";
import AppLayout from "@/components/layout/AppLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FileText, Plus, Send, Eye, Trash2, Loader2, Link as LinkIcon, Copy, Download, ClipboardList, Calendar } from "lucide-react";
import { EmptyState } from "@/components/ui/empty-state";
import { Skeleton } from "@/components/ui/skeleton";
import { PREMADE_TEMPLATES } from "@/lib/premade-contract-templates";
import { CLEANING_CONTRACT_TEMPLATES } from "@/lib/industries/cleaning/templates";
import { PI_CONTRACT_TEMPLATES } from "@/lib/industries/private-investigation/templates";
import { EVENT_CONTRACT_TEMPLATES } from "@/lib/industries/event-staffing/templates";
import { CONSTRUCTION_CONTRACT_TEMPLATES } from "@/lib/industries/construction/templates";
import { LANDSCAPING_CONTRACT_TEMPLATES } from "@/lib/industries/landscaping/templates";
import { PEST_CONTROL_CONTRACT_TEMPLATES } from "@/lib/industries/pest-control/templates";
import { HOME_SERVICES_CONTRACT_TEMPLATES } from "@/lib/industries/home-services/templates";
import { POWER_WASHING_CONTRACT_TEMPLATES } from "@/lib/industries/power-washing/templates";
import { useStaffIndustryView } from "@/hooks/useStaffIndustryView";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/contexts/AuthContext";
import { useDemoMode } from "@/hooks/useDemoMode";
import { Navigate, Link } from "@/lib/router-compat";
import { useCompanySettings } from "@/hooks/useCompanySettings";
import { useContractTemplates } from "@/hooks/useContractTemplates";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import SignaturePad from "@/components/contract/SignaturePad";


interface Proposal {
  id: string;
  address: string;
  property_type: string;
  officers: number;
  hours_of_coverage: number;
  days_of_coverage: number;
  hourly_rate: number;
  armed_status: string;
  notes: string | null;
  executive_summary: string | null;
  staffing_recommendation: string | null;
  security_actions: string | null;
  implementation_plan: string | null;
  expected_outcomes: string | null;
  created_at: string;
}

interface Contract {
  id: string;
  title: string;
  client_name: string;
  client_email: string | null;
  client_company: string | null;
  content: string;
  status: string;
  signature_data: string | null;
  signer_name: string | null;
  signed_at: string | null;
  creator_signature_data: string | null;
  creator_signer_name: string | null;
  creator_signed_at: string | null;
  signed_pdf_path: string | null;
  access_token: string;
  created_at: string;
}

const ContractGenerator = () => {
  const { user, subscription } = useAuth();
  const { isDemoUser, hasAccess, unlockFeature } = useDemoMode();
  const { settings } = useCompanySettings();
  const { templates } = useContractTemplates();
  const { effectiveIndustry } = useStaffIndustryView();
  const isCleaning = effectiveIndustry === "cleaning" || effectiveIndustry === "commercial-cleaning";
  const isPI = effectiveIndustry === "private-investigation";
  const isEvent = effectiveIndustry === "event-staffing";
  const isConstruction = effectiveIndustry === "construction-services";
  const isLandscaping = effectiveIndustry === "landscaping";
  const isPestControl = effectiveIndustry === "pest-control";
  const isHomeServices = effectiveIndustry === "home-services";
  const isPowerWashing = effectiveIndustry === "power-washing";
  const activePremadeTemplates = isPowerWashing ? POWER_WASHING_CONTRACT_TEMPLATES : isHomeServices ? HOME_SERVICES_CONTRACT_TEMPLATES : isPestControl ? PEST_CONTROL_CONTRACT_TEMPLATES : isLandscaping ? LANDSCAPING_CONTRACT_TEMPLATES : isConstruction ? CONSTRUCTION_CONTRACT_TEMPLATES : isEvent ? EVENT_CONTRACT_TEMPLATES : isPI ? PI_CONTRACT_TEMPLATES : isCleaning ? CLEANING_CONTRACT_TEMPLATES : PREMADE_TEMPLATES;
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [proposals, setProposals] = useState<Proposal[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // New contract form
  const [selectedProposal, setSelectedProposal] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [title, setTitle] = useState("");
  const [clientName, setClientName] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [clientCompany, setClientCompany] = useState("");
  const [clientPhone, setClientPhone] = useState("");
  const [projectDescription, setProjectDescription] = useState("");
  const [scopeOfWork, setScopeOfWork] = useState("");
  const [estimatedHours, setEstimatedHours] = useState("");
  const [estimatedCost, setEstimatedCost] = useState("");
  const [proposalNotes, setProposalNotes] = useState("");
  const [contractContent, setContractContent] = useState("");
  const [includeContractLength, setIncludeContractLength] = useState(false);
  const [contractLength, setContractLength] = useState("");
  const [contractLengthUnit, setContractLengthUnit] = useState("months");
  const [creatorSignature, setCreatorSignature] = useState<string | null>(null);
  const [creatorName, setCreatorName] = useState(settings.company_name || "");
  const [isCreating, setIsCreating] = useState(false);

  // Preview
  const [previewContract, setPreviewContract] = useState<Contract | null>(null);

  const fetchContracts = async () => {
    if (!user) return;
    const { data } = await supabase
      .from("contracts")
      .select("*")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false })
      .limit(100);
    setContracts((data as Contract[]) || []);
    setIsLoading(false);
  };

  const fetchProposals = async () => {
    if (!user) return;
    
    // Map effectiveIndustry to DB values
    const industryFilter = isEvent
      ? "event-staffing"
      : isPI 
        ? "private-investigation"
        : isCleaning
          ? "cleaning"
          : "security";
    
    let query = supabase
      .from("proposals")
      .select("*")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false })
      .limit(100);
    
    // Filter by industry (older proposals without industry field are treated as security)
    if (industryFilter === "security") {
      query = query.or(`industry.eq.security,industry.is.null`);
    } else {
      query = query.eq("industry", industryFilter);
    }
    
    const { data } = await query;
    setProposals((data as Proposal[]) || []);
  };

  useEffect(() => { fetchContracts(); fetchProposals(); }, [user, isPI, isCleaning, isEvent]);

  const handleProposalSelect = (proposalId: string) => {
    setSelectedProposal(proposalId);
    if (proposalId === "none") {
      return;
    }
    const proposal = proposals.find((p) => p.id === proposalId);
    if (!proposal) return;

    // Auto-fill fields from proposal data
    const weeklyHours = proposal.officers * proposal.hours_of_coverage * proposal.days_of_coverage;
    const weeklyCost = weeklyHours * proposal.hourly_rate;

    setProjectDescription(
      isEvent
        ? `Event staffing services for ${proposal.property_type} at ${proposal.address}`
        : isPI
          ? `Private investigation services — ${proposal.property_type} case at ${proposal.address}`
          : isCleaning
            ? `Commercial cleaning services for ${proposal.property_type} at ${proposal.address}`
            : `${proposal.armed_status.charAt(0).toUpperCase() + proposal.armed_status.slice(1)} security services for ${proposal.property_type} at ${proposal.address}`
    );
    setScopeOfWork(
      [
        proposal.security_actions && `${isEvent ? 'Shift Schedule' : isPI ? 'Investigation Actions' : isCleaning ? 'Cleaning Actions' : 'Security Actions'}:\n${proposal.security_actions}`,
        proposal.staffing_recommendation && `${isEvent ? 'Crew Plan' : isPI ? 'Investigation Team' : 'Staffing'}:\n${proposal.staffing_recommendation}`,
        proposal.implementation_plan && `${isEvent ? 'Labor Cost Breakdown' : 'Implementation Plan'}:\n${proposal.implementation_plan}`,
      ].filter(Boolean).join("\n\n") || ""
    );
    setEstimatedHours(String(weeklyHours));
    setEstimatedCost(weeklyCost.toLocaleString("en-US", { minimumFractionDigits: 2 }));
    setProposalNotes(proposal.notes || "");

    if (!title) setTitle(
      isEvent
        ? `Event Staffing Services — ${proposal.address}`
        : isPI
          ? `Investigation Services — ${proposal.address}`
          : isCleaning
            ? `Cleaning Services — ${proposal.address}`
            : `Security Services — ${proposal.address}`
    );

    toast({ title: "Proposal data loaded", description: `Fields populated from ${proposal.address} proposal.` });

    // Re-fill template if one is selected
    if (selectedTemplate) {
      const premade = activePremadeTemplates.find((t) => `premade-${t.name}` === selectedTemplate);
      const content = premade ? premade.content : templates.find((t) => t.id === selectedTemplate)?.content;
      if (content) {
        setTimeout(() => {
          setContractContent(fillTemplate(content));
        }, 0);
      }
    }
  };

  const fillTemplate = (templateContent: string) => {
    const now = new Date().toLocaleDateString();
    const contractLengthValue = includeContractLength && contractLength ? `${contractLength} ${contractLengthUnit}` : "";
    
    let result = templateContent
      .replace(/\{\{company_name\}\}/g, settings.company_name || "Your Company")
      .replace(/\{\{company_address\}\}/g, settings.company_address || "")
      .replace(/\{\{company_phone\}\}/g, settings.company_phone || "")
      .replace(/\{\{company_email\}\}/g, settings.company_email || "")
      .replace(/\{\{client_name\}\}/g, clientName || "Client Name")
      .replace(/\{\{client_company\}\}/g, clientCompany || "")
      .replace(/\{\{client_email\}\}/g, clientEmail || "")
      .replace(/\{\{client_phone\}\}/g, clientPhone || "")
      .replace(/\{\{date\}\}/g, now)
      .replace(/\{\{payment_terms\}\}/g, settings.default_payment_terms || "Net 30")
      .replace(/\{\{terms_and_conditions\}\}/g, settings.default_terms || "")
      .replace(/\{\{project_description\}\}/g, projectDescription || "")
      .replace(/\{\{scope_of_work\}\}/g, scopeOfWork || "")
      .replace(/\{\{estimated_hours\}\}/g, estimatedHours || "")
      .replace(/\{\{estimated_cost\}\}/g, estimatedCost || "")
      .replace(/\{\{proposal_notes\}\}/g, proposalNotes || "")
      .replace(/\{\{contract_length\}\}/g, contractLengthValue);

    // If contract length is enabled but the template didn't have the placeholder, inject it before SIGNATURES or at the end
    if (includeContractLength && contractLength && !templateContent.includes("{{contract_length}}")) {
      const lengthSection = `\n──────────────────────────────────────────────────\n\nCONTRACT DURATION\n\nThis contract shall remain in effect for a period of ${contractLength} ${contractLengthUnit} from the date of execution, unless terminated earlier in accordance with the terms herein.\n`;
      
      // Try to insert before the SIGNATURES section
      const signaturesMatch = result.match(/\n(──+\s*\n+\s*\d+\.\s*SIGNATURES)/i);
      if (signaturesMatch && signaturesMatch.index !== undefined) {
        result = result.slice(0, signaturesMatch.index) + lengthSection + result.slice(signaturesMatch.index);
      } else {
        // Fallback: insert before the last separator or at end
        const lastSigIndex = result.lastIndexOf("SIGNATURES");
        if (lastSigIndex !== -1) {
          const insertPoint = result.lastIndexOf("──", lastSigIndex);
          if (insertPoint !== -1) {
            result = result.slice(0, insertPoint) + lengthSection.trim() + "\n\n" + result.slice(insertPoint);
          } else {
            result += lengthSection;
          }
        } else {
          result += lengthSection;
        }
      }
    }

    return result;
  };

  const handleTemplateSelect = (templateId: string) => {
    setSelectedTemplate(templateId);
    // Check pre-made templates first
    const premade = activePremadeTemplates.find((t) => `premade-${t.name}` === templateId);
    if (premade) {
      setTitle(premade.name);
      setContractContent(fillTemplate(premade.content));
      return;
    }
    const template = templates.find((t) => t.id === templateId);
    if (template) {
      setTitle(template.name);
      setContractContent(fillTemplate(template.content));
    }
  };

  // Re-fill when client info changes
  useEffect(() => {
    if (selectedTemplate) {
      const premade = activePremadeTemplates.find((t) => `premade-${t.name}` === selectedTemplate);
      if (premade) {
        setContractContent(fillTemplate(premade.content));
        return;
      }
      const template = templates.find((t) => t.id === selectedTemplate);
      if (template) setContractContent(fillTemplate(template.content));
    }
  }, [clientName, clientCompany, clientEmail, clientPhone, projectDescription, scopeOfWork, estimatedHours, estimatedCost, proposalNotes, includeContractLength, contractLength, contractLengthUnit]);

  const createContract = async () => {
    if (!user || !title.trim() || !clientName.trim() || !contractContent.trim()) {
      toast({ title: "Fill in all required fields", variant: "destructive" });
      return;
    }
    if (!creatorSignature) {
      toast({ title: "Please sign the contract before creating", variant: "destructive" });
      return;
    }
    if (!creatorName.trim()) {
      toast({ title: "Please enter your name for the signature", variant: "destructive" });
      return;
    }
    setIsCreating(true);
    const { error } = await supabase.from("contracts").insert({
      user_id: user.id,
      template_id: selectedTemplate || null,
      proposal_id: selectedProposal && selectedProposal !== "none" ? selectedProposal : null,
      title: title.trim(),
      client_name: clientName.trim(),
      client_email: clientEmail.trim() || null,
      client_company: clientCompany.trim() || null,
      content: contractContent,
      status: "draft",
      creator_signature_data: creatorSignature,
      creator_signer_name: creatorName.trim(),
      creator_signed_at: new Date().toISOString(),
    } as any);
    if (error) {
      toast({ title: "Error creating contract", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Contract created" });
      setTitle(""); setClientName(""); setClientEmail(""); setClientCompany("");
      setClientPhone(""); setProjectDescription(""); setScopeOfWork("");
      setEstimatedHours(""); setEstimatedCost(""); setProposalNotes("");
      setContractContent(""); setSelectedTemplate(""); setSelectedProposal("");
      setCreatorSignature(null); setCreatorName(settings.company_name || "");
      setIncludeContractLength(false); setContractLength(""); setContractLengthUnit("months");
      await fetchContracts();
    }
    setIsCreating(false);
  };

  const copySigningLink = (contract: Contract) => {
    const url = `${window.location.origin}/sign/${contract.access_token}`;
    navigator.clipboard.writeText(url);
    toast({ title: "Signing link copied!" });
    // Mark as sent
    if (contract.status === "draft") {
      supabase.from("contracts").update({ status: "sent", sent_at: new Date().toISOString() }).eq("id", contract.id).then(() => fetchContracts());
    }
  };

  const deleteContract = async (id: string) => {
    await supabase.from("contracts").delete().eq("id", id);
    toast({ title: "Contract deleted" });
    fetchContracts();
  };

  const exportPdf = async (contract: Contract) => {
    const { default: jsPDF } = await import("jspdf");
    const doc = new jsPDF();
    const margin = 20;
    const pageWidth = doc.internal.pageSize.getWidth() - margin * 2;
    const lines = doc.splitTextToSize(contract.content, pageWidth);
    let y = margin;

    doc.setFontSize(10);
    for (const line of lines) {
      if (y > 270) { doc.addPage(); y = margin; }
      doc.text(line, margin, y);
      y += 5;
    }

    // Creator signature
    if (contract.creator_signature_data) {
      if (y > 210) { doc.addPage(); y = margin; }
      y += 10;
      doc.setFontSize(11);
      doc.text("--- Creator Signature ---", margin, y);
      y += 7;
      doc.text(`Signed by: ${contract.creator_signer_name || "Creator"}`, margin, y);
      y += 6;
      doc.text(`Date: ${contract.creator_signed_at ? new Date(contract.creator_signed_at).toLocaleString() : ""}`, margin, y);
      y += 8;
      doc.addImage(contract.creator_signature_data, "PNG", margin, y, 60, 25);
      y += 30;
    }

    // Client signature
    if (contract.signature_data) {
      if (y > 210) { doc.addPage(); y = margin; }
      y += 10;
      doc.setFontSize(11);
      doc.text("--- Client Signature ---", margin, y);
      y += 7;
      doc.text(`Signed by: ${contract.signer_name || "Client"}`, margin, y);
      y += 6;
      doc.text(`Date: ${contract.signed_at ? new Date(contract.signed_at).toLocaleString() : ""}`, margin, y);
      y += 8;
      doc.addImage(contract.signature_data, "PNG", margin, y, 60, 25);
    }

    doc.save(`${contract.title.replace(/\s+/g, "_")}.pdf`);
  };

  const statusColor = (s: string) => {
    if (s === "signed") return "default";
    if (s === "sent") return "secondary";
    return "outline";
  };

  const canAccessContracts = isDemoUser
    ? hasAccess("professional")
    : subscription.tier === "professional" || subscription.tier === "agency" || subscription.tier === "enterprise" || subscription.tier === "testing";

  if (!canAccessContracts) {
    return (
      <AppLayout>
        <div className="max-w-lg mx-auto text-center py-20 space-y-4">
          <FileText className="h-12 w-12 text-muted-foreground mx-auto" />
          <h2 className="text-2xl font-bold text-foreground">Contract Generator</h2>
          <p className="text-muted-foreground">
            {isDemoUser
              ? "Click below to unlock this feature for demo."
              : "The Contract Generator is available on the Professional plan and above. Upgrade your plan to create, send, and manage legally formatted contracts."
            }
          </p>
          {isDemoUser ? (
            <Button onClick={() => unlockFeature("professional")} className="gradient-primary text-primary-foreground shadow-glow hover:opacity-90">
              Unlock for Demo
            </Button>
          ) : (
            <Button asChild className="gradient-primary text-primary-foreground shadow-glow hover:opacity-90">
              <Link to="/pricing">Upgrade Plan</Link>
            </Button>
          )}
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="max-w-5xl mx-auto space-y-6">
        <h1 className="text-3xl font-bold text-foreground">Contract Generator</h1>

        {/* Create New Contract */}
        <Card className="border-border bg-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Plus className="h-5 w-5 text-primary" /> New Contract</CardTitle>
            <CardDescription>Select a template and fill in client details to generate a contract.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Proposal auto-fill */}
            {proposals.length > 0 && (
              <div className="rounded-lg border border-primary/30 bg-primary/5 p-4">
                <Label className="flex items-center gap-2 text-sm font-medium mb-2">
                  <ClipboardList className="h-4 w-4 text-primary" /> Auto-fill from Proposal
                </Label>
                <Select value={selectedProposal} onValueChange={handleProposalSelect}>
                  <SelectTrigger><SelectValue placeholder="Select a proposal to auto-fill fields..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— None —</SelectItem>
                    {proposals.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.address} — {p.property_type} ({new Date(p.created_at).toLocaleDateString()})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">Selecting a proposal will populate project details, scope, hours, and cost automatically.</p>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Template</Label>
                <Select value={selectedTemplate} onValueChange={handleTemplateSelect}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Select a template..." /></SelectTrigger>
                  <SelectContent>
                    {activePremadeTemplates.map((t) => (
                      <SelectItem key={`premade-${t.name}`} value={`premade-${t.name}`}>📋 {t.name}</SelectItem>
                    ))}
                    {templates.length > 0 && activePremadeTemplates.length > 0 && (
                      <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">Your Templates</div>
                    )}
                    {templates.map((t) => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {templates.length === 0 && activePremadeTemplates.length === 0 && (
                  <p className="text-xs text-muted-foreground mt-1">No templates yet. <a href="/settings" className="text-primary underline">Create one in Settings</a>.</p>
                )}
              </div>
              <div>
                <Label>Contract Title *</Label>
                <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={isCleaning ? "Janitorial Service Agreement" : "Security Services Agreement"} className="mt-1" />
              </div>
              <div>
                <Label>Client Name *</Label>
                <Input value={clientName} onChange={(e) => setClientName(e.target.value)} placeholder="John Smith" className="mt-1" />
              </div>
              <div>
                <Label>Client Email</Label>
                <Input value={clientEmail} onChange={(e) => setClientEmail(e.target.value)} placeholder="client@example.com" className="mt-1" />
              </div>
              <div>
                <Label>Client Company</Label>
                <Input value={clientCompany} onChange={(e) => setClientCompany(e.target.value)} placeholder="Acme Corp" className="mt-1" />
              </div>
              <div>
                <Label>Client Phone</Label>
                <Input value={clientPhone} onChange={(e) => setClientPhone(e.target.value)} placeholder="(555) 987-6543" className="mt-1" />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Estimated Hours</Label>
                <Input value={estimatedHours} onChange={(e) => setEstimatedHours(e.target.value)} placeholder="160" className="mt-1" />
              </div>
              <div>
                <Label>Estimated Cost ($)</Label>
                <Input value={estimatedCost} onChange={(e) => setEstimatedCost(e.target.value)} placeholder="4,000.00" className="mt-1" />
              </div>
            </div>
            <div>
              <Label>Project Description</Label>
              <Textarea value={projectDescription} onChange={(e) => setProjectDescription(e.target.value)} placeholder={isCleaning ? "Brief description of the cleaning project..." : "Brief description of the security project..."} className="mt-1" rows={2} />
            </div>
            <div>
              <Label>Scope of Work</Label>
              <Textarea value={scopeOfWork} onChange={(e) => setScopeOfWork(e.target.value)} placeholder="Detailed scope of services to be provided..." className="mt-1" rows={3} />
            </div>
            <div>
              <Label>Additional Notes</Label>
              <Textarea value={proposalNotes} onChange={(e) => setProposalNotes(e.target.value)} placeholder="Any additional notes or special conditions..." className="mt-1" rows={2} />
            </div>
            {/* Contract Length (Optional) */}
            <div className="rounded-lg border border-border p-4 space-y-3">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="includeContractLength"
                  checked={includeContractLength}
                  onCheckedChange={(checked) => setIncludeContractLength(!!checked)}
                />
                <Label htmlFor="includeContractLength" className="flex items-center gap-2 cursor-pointer">
                  <Calendar className="h-4 w-4 text-primary" />
                  Include Contract Length / Duration
                </Label>
              </div>
              {includeContractLength && (
                <div className="grid grid-cols-2 gap-4 pl-6">
                  <div>
                    <Label>Duration</Label>
                    <Input
                      type="number"
                      min="1"
                      value={contractLength}
                      onChange={(e) => setContractLength(e.target.value)}
                      placeholder="e.g. 12"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Unit</Label>
                    <Select value={contractLengthUnit} onValueChange={setContractLengthUnit}>
                      <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="days">Days</SelectItem>
                        <SelectItem value="weeks">Weeks</SelectItem>
                        <SelectItem value="months">Months</SelectItem>
                        <SelectItem value="years">Years</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <p className="col-span-2 text-xs text-muted-foreground">
                    Use <code className="bg-secondary px-1 rounded text-xs">{"{{contract_length}}"}</code> in your template to insert the duration.
                  </p>
                </div>
              )}
            </div>

            <div>
              <Label>Contract Content *</Label>
              <Textarea value={contractContent} onChange={(e) => setContractContent(e.target.value)} className="mt-1 font-mono text-sm" rows={12} placeholder="Select a template or type contract content..." />
            </div>

            {/* Creator Signature */}
            <Card className="border-primary/30 bg-primary/5">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Your Signature (Creator) *</CardTitle>
                <CardDescription>Sign this contract before sending it to the client.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Label>Your Full Name *</Label>
                  <Input value={creatorName} onChange={(e) => setCreatorName(e.target.value)} placeholder="Your name" className="mt-1" />
                </div>
                <div>
                  <Label>Your Signature *</Label>
                  <div className="mt-1">
                    <SignaturePad onSignatureChange={setCreatorSignature} height={150} />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Button onClick={createContract} disabled={isCreating} className="gap-2">
              {isCreating ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
              Create Contract
            </Button>
          </CardContent>
        </Card>

        {/* Contract List */}
        <Card className="border-border bg-card">
          <CardHeader>
            <CardTitle>Your Contracts</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-3" aria-label="Loading contracts">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="flex items-center justify-between p-4 rounded-lg border border-border bg-secondary/30">
                    <div className="flex-1 min-w-0 space-y-2">
                      <Skeleton className="h-4 w-2/3" />
                      <Skeleton className="h-3 w-1/3" />
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <Skeleton className="h-8 w-8 rounded-md" />
                      <Skeleton className="h-8 w-8 rounded-md" />
                      <Skeleton className="h-8 w-8 rounded-md" />
                    </div>
                  </div>
                ))}
              </div>
            ) : contracts.length === 0 ? (
              <EmptyState
                icon={FileText}
                title="No contracts yet"
                description="Pick a template above, fill in the client details, and your first signed-ready contract will appear here."
                size="sm"
              />
            ) : (
              <div className="space-y-3">
                {contracts.map((c) => (
                  <div key={c.id} className="flex items-center justify-between p-4 rounded-lg border border-border bg-secondary/30">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-foreground truncate">{c.title}</p>
                        <Badge variant={statusColor(c.status)}>{c.status}</Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">{c.client_name}{c.client_company ? ` — ${c.client_company}` : ""}</p>
                      <p className="text-xs text-muted-foreground">{new Date(c.created_at).toLocaleDateString()}</p>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <Button variant="ghost" size="icon" onClick={() => setPreviewContract(c)} title="Preview"><Eye className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" onClick={() => copySigningLink(c)} title="Copy signing link"><LinkIcon className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" onClick={() => exportPdf(c)} title="Download PDF"><Download className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" onClick={() => deleteContract(c.id)} className="text-destructive" title="Delete"><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Preview Dialog */}
        <Dialog open={!!previewContract} onOpenChange={(open) => !open && setPreviewContract(null)}>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {previewContract?.title}
                <Badge variant={statusColor(previewContract?.status || "")}>{previewContract?.status}</Badge>
              </DialogTitle>
            </DialogHeader>
            {previewContract && (
              <div className="space-y-4">
                <pre className="whitespace-pre-wrap text-sm font-mono bg-secondary/50 p-4 rounded-lg border border-border">{previewContract.content}</pre>
                {previewContract.creator_signature_data && (
                  <div className="border border-border rounded-lg p-4">
                    <p className="text-xs font-semibold uppercase text-muted-foreground mb-1">Creator Signature</p>
                    <p className="text-sm font-medium mb-1">Signed by: {previewContract.creator_signer_name}</p>
                    <p className="text-xs text-muted-foreground mb-2">{previewContract.creator_signed_at && new Date(previewContract.creator_signed_at).toLocaleString()}</p>
                    <img src={previewContract.creator_signature_data} alt="Creator Signature" className="max-h-24 border border-border rounded" />
                  </div>
                )}
                {previewContract.signature_data && (
                  <div className="border border-border rounded-lg p-4">
                    <p className="text-xs font-semibold uppercase text-muted-foreground mb-1">Client Signature</p>
                    <p className="text-sm font-medium mb-1">Signed by: {previewContract.signer_name}</p>
                    <p className="text-xs text-muted-foreground mb-2">{previewContract.signed_at && new Date(previewContract.signed_at).toLocaleString()}</p>
                    <img src={previewContract.signature_data} alt="Client Signature" className="max-h-24 border border-border rounded" />
                  </div>
                )}
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" onClick={() => copySigningLink(previewContract)} className="gap-1"><Copy className="h-4 w-4" /> Copy Signing Link</Button>
                  <Button variant="outline" onClick={() => exportPdf(previewContract)} className="gap-1"><Download className="h-4 w-4" /> Export PDF</Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AppLayout>
  );
};

export default ContractGenerator;
